import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Megaphone, Send, Clock, AlertTriangle, Info, CheckCircle } from 'lucide-react';

const PlatformAnnouncements = () => {
  const { apiFetch } = usePlatformAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', priority: 'info' });

  const fetchAnnouncements = async () => {
    try {
      const res = await apiFetch('/announcements');
      setAnnouncements(res.data.announcements);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title || !form.message) return toast.error('Title and message required');
    setSending(true);
    try {
      const res = await apiFetch('/announcements', { method: 'POST', body: JSON.stringify(form) });
      toast.success(`Announcement sent to ${res.data.notified_admins} school admin(s)!`);
      setForm({ title: '', message: '', priority: 'info' });
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  const priorityIcons = { info: Info, warning: AlertTriangle, success: CheckCircle };
  const priorityColors = { info: 'text-blue-400 bg-blue-600/20', warning: 'text-amber-400 bg-amber-600/20', success: 'text-emerald-400 bg-emerald-600/20' };

  return (
    <div data-testid="platform-announcements">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Announcements</h1>
        <p className="text-slate-400">Send platform-wide announcements to all school admins</p>
      </div>

      {/* Compose */}
      <Card className="bg-slate-900 border-slate-800 mb-8">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Megaphone className="h-5 w-5 text-indigo-400" /> New Announcement</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSend} className="space-y-4" data-testid="announcement-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input placeholder="e.g. Scheduled Maintenance on March 1st" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="announcement-title" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="announcement-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Message</Label>
              <textarea
                placeholder="Write your announcement here..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="flex w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                data-testid="announcement-message"
              />
            </div>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={sending} data-testid="announcement-send-btn">
              <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : 'Send to All Schools'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* History */}
      <h2 className="text-xl font-bold text-white mb-4">Sent Announcements</h2>
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" /></div>
      ) : announcements.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-8 text-center text-slate-500">No announcements sent yet</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => {
            const Icon = priorityIcons[a.priority] || Info;
            const color = priorityColors[a.priority] || priorityColors.info;
            return (
              <Card key={a.announcement_id} className="bg-slate-900 border-slate-800" data-testid={`announcement-${a.announcement_id}`}>
                <CardContent className="p-5 flex items-start gap-4">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color.split(' ')[1]}`}>
                    <Icon className={`h-5 w-5 ${color.split(' ')[0]}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-white font-semibold">{a.title}</h3>
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(a.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <p className="text-slate-400 text-sm">{a.message}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlatformAnnouncements;
